// Global Instructions Rule Applied!
import type { GlobalSearchResult } from '../services/utilitiesService.ts'

/**
 * API response bucket keys from GET global search (grouped results).
 * Must match `search_global` in backend and `GlobalSearchResponse.data` in utilitiesService.
 * The order of this array is the order the sections show in the navbar dropdown.
 */
export const GLOBAL_SEARCH_BUCKET_KEYS = [
  'funds',
  'consolidated_funds',
  'accounts',
  'investors',
  'deals',
  'contacts'
] as const

export type GlobalSearchBucketKey = (typeof GLOBAL_SEARCH_BUCKET_KEYS)[number]

export type GlobalSearchResultsByBucket = Record<GlobalSearchBucketKey, GlobalSearchResult[]>

/** Section headings of the navbar dropdown. */
export const GLOBAL_SEARCH_BUCKET_LABELS: Record<GlobalSearchBucketKey, string> = {
  funds: 'Funds',
  consolidated_funds: 'Consolidated Funds',
  accounts: 'Accounts',
  investors: 'Investors',
  deals: 'Deals',
  contacts: 'Contacts'
}

/**
 * `search_index.entity_type` values from RPC (see `function_search_search_index.sql` / populate_search_index).
 * Maps to tab `module` for `openFormInTab` — not the same as response bucket keys (e.g. account → companies).
 * Tab modules must match `tabSlice` / `module` strings.
 */
const GLOBAL_SEARCH_ENTITY_TYPE_TO_TAB_MODULE: Record<string, string> = {
  deal: 'deals',
  account: 'companies',
  investor: 'companies',
  contact: 'contacts',
  fund: 'funds',
  fund_group: 'consolidated-funds'
}

/**
 * Buckets whose rows show the RPC `subtitle` under the title in the navbar
 * dropdown (contacts: email; funds and consolidated funds: legal name). The
 * other buckets' subtitle duplicates the title (deals) or is noise at dropdown
 * density (accounts).
 */
export const GLOBAL_SEARCH_SUBTITLE_BUCKET_KEYS: ReadonlySet<GlobalSearchBucketKey> = new Set([
  'contacts',
  'funds',
  'consolidated_funds'
])

export const GLOBAL_SEARCH_DEBOUNCE_MS = 500

/** Max rows shown per category in the navbar dropdown before "View more". */
export const GLOBAL_SEARCH_MAX_VISIBLE_PER_BUCKET = 5

export function createEmptyGlobalSearchResults(): GlobalSearchResultsByBucket {
  return {
    funds: [],
    consolidated_funds: [],
    accounts: [],
    investors: [],
    deals: [],
    contacts: []
  }
}

export function hasAnyGlobalSearchResults(data: GlobalSearchResultsByBucket | undefined): boolean {
  if (!data) return false
  return GLOBAL_SEARCH_BUCKET_KEYS.some((k) => (data[k]?.length ?? 0) > 0)
}

export function getTabModuleForSearchEntityType(entityType: string | undefined): string | undefined {
  if (!entityType) return undefined
  return GLOBAL_SEARCH_ENTITY_TYPE_TO_TAB_MODULE[entityType]
}
